import { useState, useEffect, useMemo } from 'react';
import {
  Palette,
  Sparkles,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Sliders,
  Check,
  Layout,
  Percent,
  Layers,
  Search,
  Grid,
  Columns,
  ListTree,
  Rows,
  Image as ImageIcon,
  PanelLeft,
  SlidersHorizontal,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert.jsx';
import {
  THEME_PRESETS,
  RADIUS_OPTIONS,
  HEADER_SLOT_OPTIONS,
  HERO_SLOT_OPTIONS,
  CARD_SLOT_OPTIONS,
  FILTER_SLOT_OPTIONS,
  getContrastRatio,
  calculatePriceWithMarkup,
} from './admin-presets.js';
import { LiveShopMockup } from './LiveShopMockup.jsx';

function parseMarkupPercent(setting) {
  if (setting === undefined || setting === null) return 0;
  if (typeof setting === 'number') return setting;
  if (typeof setting === 'string') {
    const num = parseFloat(setting);
    return isNaN(num) ? 0 : num;
  }
  if (typeof setting === 'object') {
    if (setting.percent !== undefined && setting.percent !== null) return Number(setting.percent);
    if (setting.markup !== undefined && setting.markup !== null) return Number((setting.markup * 100).toFixed(2));
  }
  return 0;
}

export function ThemeCustomizerTab({
  tenants = [],
  activeTenant,
  onSelectTenant,
  onUpdateTenant,
  saving = false,
}) {
  const [selectedTenantId, setSelectedTenantId] = useState(activeTenant?.id || (tenants[0]?.id ?? ''));

  // Synchronize activeTenant prop changes
  useEffect(() => {
    if (activeTenant?.id) {
      setSelectedTenantId(activeTenant.id);
    }
  }, [activeTenant?.id]);

  // Active tenant object
  const currentTenant = useMemo(() => {
    return tenants.find((t) => t.id === selectedTenantId) || activeTenant || tenants[0] || null;
  }, [tenants, selectedTenantId, activeTenant]);

  // Draft Theme State
  const [draftTheme, setDraftTheme] = useState({
    primaryColor: '#0056b3',
    secondaryColor: '#5f5e5f',
    accentColor: '#ff9900',
    borderRadius: '0.375rem',
    slogan: '',
    logoUrl: '',
    layout_header: 'slim',
    layout_hero: 'portal_search',
    product_card_variant: 'specs_heavy',
    filter_layout: 'sidebar',
    pricingMarkupPercent: 0,
  });

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Sync draft theme when currentTenant changes
  useEffect(() => {
    if (currentTenant) {
      setDraftTheme({
        primaryColor: currentTenant.primaryColor || '#0056b3',
        secondaryColor: currentTenant.secondaryColor || '#5f5e5f',
        accentColor: currentTenant.accentColor || currentTenant.settings?.branding?.accentColor || '#ff9900',
        borderRadius: currentTenant.settings?.theme_radius || '0.375rem',
        slogan: currentTenant.settings?.branding?.slogan || currentTenant.slogan || '',
        logoUrl: currentTenant.logoUrl || '',
        layout_header: currentTenant.settings?.layout_header || currentTenant.layout_header || 'slim',
        layout_hero: currentTenant.settings?.layout_hero || currentTenant.layout_hero || 'portal_search',
        product_card_variant: currentTenant.settings?.product_card_variant || currentTenant.product_card_variant || 'specs_heavy',
        filter_layout: currentTenant.settings?.filter_layout || currentTenant.filter_layout || 'sidebar',
        pricingMarkupPercent: parseMarkupPercent(currentTenant.settings?.pricing_markup_percent),
      });
      setSaveSuccess(false);
      setSaveError(null);
    }
  }, [currentTenant]);

  // Contrast Calculation
  const contrastWithWhite = useMemo(() => {
    return getContrastRatio(draftTheme.primaryColor, '#ffffff');
  }, [draftTheme.primaryColor]);

  const passesWCAG_AA = contrastWithWhite >= 4.5;
  const passesWCAG_AAA = contrastWithWhite >= 7.0;

  // Apply Preset
  const handleApplyPreset = (preset) => {
    setDraftTheme((prev) => ({
      ...prev,
      primaryColor: preset.primaryColor,
      secondaryColor: preset.secondaryColor,
      accentColor: preset.accentColor,
      borderRadius: preset.borderRadius,
    }));
    setSaveSuccess(false);
  };

  // Hex color normalizer / validator
  const handleHexChange = (field, rawValue) => {
    let val = rawValue.trim();
    if (!val.startsWith('#')) val = `#${val}`;
    setDraftTheme((prev) => ({ ...prev, [field]: val }));
    setSaveSuccess(false);
  };

  // Reset to original tenant branding
  const handleReset = () => {
    if (!currentTenant) return;
    setDraftTheme({
      primaryColor: currentTenant.primaryColor || '#0056b3',
      secondaryColor: currentTenant.secondaryColor || '#5f5e5f',
      accentColor: currentTenant.accentColor || currentTenant.settings?.branding?.accentColor || '#ff9900',
      borderRadius: currentTenant.settings?.theme_radius || '0.375rem',
      slogan: currentTenant.settings?.branding?.slogan || currentTenant.slogan || '',
      logoUrl: currentTenant.logoUrl || '',
      layout_header: currentTenant.settings?.layout_header || currentTenant.layout_header || 'slim',
      layout_hero: currentTenant.settings?.layout_hero || currentTenant.layout_hero || 'portal_search',
      product_card_variant: currentTenant.settings?.product_card_variant || currentTenant.product_card_variant || 'specs_heavy',
      filter_layout: currentTenant.settings?.filter_layout || currentTenant.filter_layout || 'sidebar',
      pricingMarkupPercent: parseMarkupPercent(currentTenant.settings?.pricing_markup_percent),
    });
    setSaveSuccess(false);
    setSaveError(null);
  };

  // Save changes via PUT /api/admin/tenants/:id
  const handleSave = async () => {
    if (!currentTenant) return;
    try {
      setSaveError(null);
      setSaveSuccess(false);

      const markupVal = parseFloat(draftTheme.pricingMarkupPercent) || 0;
      const payload = {
        primaryColor: draftTheme.primaryColor,
        secondaryColor: draftTheme.secondaryColor,
        logoUrl: draftTheme.logoUrl || undefined,
        settings: {
          ...(currentTenant.settings || {}),
          theme_radius: draftTheme.borderRadius,
          layout_header: draftTheme.layout_header,
          layout_hero: draftTheme.layout_hero,
          product_card_variant: draftTheme.product_card_variant,
          filter_layout: draftTheme.filter_layout,
          pricing_markup_percent: {
            markup: Number((markupVal / 100).toFixed(4)),
            percent: markupVal,
          },
          branding: {
            ...(currentTenant.settings?.branding || {}),
            slogan: draftTheme.slogan,
            accentColor: draftTheme.accentColor,
          },
        },
      };

      await onUpdateTenant(currentTenant.id, payload);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      setSaveError(err.message || 'Fehler beim Speichern des Themes');
    }
  };

  if (!currentTenant && tenants.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500">
        Keine Mandanten zum Anpassen vorhanden. Bitte erstellen Sie zuerst einen Mandanten.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tenant Selector & Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-[var(--border)] pb-4">
        <div>
          <h2 className="font-display text-xl font-bold text-[var(--foreground)]">
            Theme Customizer & Live-Vorschau
          </h2>
          <p className="text-xs text-[var(--secondary)]">
            Passen Sie Farben, Typografie, Radien und Shop-Slogans für Ihre Partner-Mandanten in Echtzeit an.
          </p>
        </div>

        {tenants.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-700">Ausgewählter Mandant:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => {
                setSelectedTenantId(e.target.value);
                const found = tenants.find((t) => t.id === e.target.value);
                if (found && onSelectTenant) onSelectTenant(found);
              }}
              className="rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-2xs focus:ring-2 focus:ring-[var(--primary)]"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Main 2-Column Layout */}
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Left Column: Theme Controls (5 cols) */}
        <div className="space-y-6 lg:col-span-5">
          {/* Notification Banners */}
          {saveSuccess && (
            <Alert variant="success" className="border-emerald-300 bg-emerald-50 text-emerald-800">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertTitle>Theme erfolgreich gespeichert!</AlertTitle>
              <AlertDescription>
                Die Änderungen wurden für {currentTenant?.name} persistiert und sind sofort im Shop aktiv.
              </AlertDescription>
            </Alert>
          )}

          {saveError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Fehler beim Speichern</AlertTitle>
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}

          {/* Palette Presets */}
          <Card className="border-[var(--border)] bg-white shadow-xs">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--primary)]" />
                <CardTitle className="text-sm font-bold">1-Klick Theme-Vorlagen</CardTitle>
              </div>
              <CardDescription className="text-xs">
                Wählen Sie aus praxiserprobten Farbpaletten für Sicherheits- & B2B-Shops.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2">
                {THEME_PRESETS.map((p) => {
                  const isMatch =
                    draftTheme.primaryColor.toLowerCase() === p.primaryColor.toLowerCase() &&
                    draftTheme.secondaryColor.toLowerCase() === p.secondaryColor.toLowerCase();
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleApplyPreset(p)}
                      className={`flex flex-col text-left rounded-lg border p-2.5 transition-all text-xs ${
                        isMatch
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5 shadow-2xs'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/80'
                      }`}
                    >
                      <div className="flex items-center justify-between font-semibold text-slate-900">
                        <span>{p.name}</span>
                        {isMatch && <Check className="h-3.5 w-3.5 text-[var(--primary)]" />}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span
                          className="h-4 w-4 rounded-full border border-slate-300 shadow-2xs"
                          style={{ backgroundColor: p.primaryColor }}
                        />
                        <span
                          className="h-4 w-4 rounded-full border border-slate-300 shadow-2xs"
                          style={{ backgroundColor: p.secondaryColor }}
                        />
                        <span
                          className="h-4 w-4 rounded-full border border-slate-300 shadow-2xs"
                          style={{ backgroundColor: p.accentColor }}
                        />
                        <span className="text-[10px] text-slate-500 ml-1 font-mono">{p.borderRadius}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Interactive Color Controls & WCAG Validator */}
          <Card className="border-[var(--border)] bg-white shadow-xs">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-[var(--primary)]" />
                <CardTitle className="text-sm font-bold">Farben & Kontrast (WCAG 2.1)</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Primary Color */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800">
                    Primärfarbe (Header, Buttons, Akzente)
                  </label>
                  <span className="text-[11px] font-mono text-slate-500">{draftTheme.primaryColor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={draftTheme.primaryColor}
                    onChange={(e) => handleHexChange('primaryColor', e.target.value)}
                    className="h-9 w-12 rounded-md border border-[var(--border)] p-0.5 cursor-pointer shrink-0"
                  />
                  <Input
                    value={draftTheme.primaryColor}
                    onChange={(e) => handleHexChange('primaryColor', e.target.value)}
                    className="font-mono text-xs"
                    placeholder="#0056b3"
                  />
                </div>
              </div>

              {/* WCAG Contrast Badge */}
              <div
                className={`rounded-lg p-3 text-xs border ${
                  passesWCAG_AA
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-300 text-amber-800'
                }`}
              >
                <div className="flex items-center justify-between font-semibold">
                  <span>Weißer Text Kontrastverhältnis:</span>
                  <span className="font-mono">{contrastWithWhite}:1</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={passesWCAG_AA ? 'success' : 'warning'} className="text-[10px]">
                    {passesWCAG_AAA ? 'WCAG AAA (Hervorragend)' : passesWCAG_AA ? 'WCAG AA (Erfüllt)' : 'WCAG Nicht Erfüllt'}
                  </Badge>
                </div>
                {!passesWCAG_AA && (
                  <p className="mt-1.5 text-[11px] leading-tight text-amber-900">
                    Achtung: Der Kontrast zu weißer Schrift liegt unter 4.5:1. Die Schaltflächentexte könnten für manche Nutzer schwer lesbar sein.
                  </p>
                )}
              </div>

              {/* Secondary Color */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800">
                    Sekundärfarbe (Footer, Hintergründe)
                  </label>
                  <span className="text-[11px] font-mono text-slate-500">{draftTheme.secondaryColor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={draftTheme.secondaryColor}
                    onChange={(e) => handleHexChange('secondaryColor', e.target.value)}
                    className="h-9 w-12 rounded-md border border-[var(--border)] p-0.5 cursor-pointer shrink-0"
                  />
                  <Input
                    value={draftTheme.secondaryColor}
                    onChange={(e) => handleHexChange('secondaryColor', e.target.value)}
                    className="font-mono text-xs"
                    placeholder="#5f5e5f"
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800">
                    Highlight- / Akzentfarbe (Badges, Warenkorb)
                  </label>
                  <span className="text-[11px] font-mono text-slate-500">{draftTheme.accentColor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={draftTheme.accentColor}
                    onChange={(e) => handleHexChange('accentColor', e.target.value)}
                    className="h-9 w-12 rounded-md border border-[var(--border)] p-0.5 cursor-pointer shrink-0"
                  />
                  <Input
                    value={draftTheme.accentColor}
                    onChange={(e) => handleHexChange('accentColor', e.target.value)}
                    className="font-mono text-xs"
                    placeholder="#ff9900"
                  />
                </div>
              </div>

              {/* Corner Radius Selector */}
              <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800">
                    Eckenabrundung (Border Radius)
                  </label>
                  <span className="text-[11px] font-mono text-slate-500">{draftTheme.borderRadius}</span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {RADIUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDraftTheme((prev) => ({ ...prev, borderRadius: opt.value }))}
                      className={`rounded-md border py-1.5 px-2 text-center text-xs font-medium transition-all ${
                        draftTheme.borderRadius === opt.value
                          ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)] font-bold'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Branding Texts */}
              <div className="space-y-3 pt-2 border-t border-[var(--border)]">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-800">Hero Slogan / Motto</label>
                  <Input
                    value={draftTheme.slogan}
                    onChange={(e) => setDraftTheme((prev) => ({ ...prev, slogan: e.target.value }))}
                    placeholder="z.B. Ihr Partner für zertifizierte Sicherheitstechnik"
                    className="text-xs"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-800">Logo URL</label>
                  <Input
                    value={draftTheme.logoUrl}
                    onChange={(e) => setDraftTheme((prev) => ({ ...prev, logoUrl: e.target.value }))}
                    placeholder="/assets/logos/partner.svg"
                    className="text-xs font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Astryx Slot Component Selectors */}
          <Card className="border-[var(--border)] bg-white shadow-xs">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Layout className="h-4 w-4 text-[var(--primary)]" />
                <CardTitle className="text-sm font-bold">Astryx Slot-Layouts & Komponenten</CardTitle>
              </div>
              <CardDescription className="text-xs">
                Wählen Sie die modularen Frontend-Varianten für Header, Hero, Produktkarten und Filter.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* 1. Header Slot Variant */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                    <Layers className="h-3.5 w-3.5 text-slate-500" />
                    Header-Layout (Header Slot)
                  </label>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {draftTheme.layout_header}
                  </Badge>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {HEADER_SLOT_OPTIONS.map((opt) => {
                    const isSelected = draftTheme.layout_header === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDraftTheme((prev) => ({ ...prev, layout_header: opt.value }))}
                        className={`flex flex-col text-left rounded-lg border p-2.5 transition-all text-xs ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-1 ring-[var(--primary)] shadow-2xs'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between font-semibold text-slate-900">
                          <span className="truncate">{opt.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />}
                        </div>
                        <p className="mt-1 text-[11px] leading-tight text-slate-500 line-clamp-2">
                          {opt.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Hero Slot Variant */}
              <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                    <Search className="h-3.5 w-3.5 text-slate-500" />
                    Hero-Banner Layout (Hero Slot)
                  </label>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {draftTheme.layout_hero}
                  </Badge>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {HERO_SLOT_OPTIONS.map((opt) => {
                    const isSelected = draftTheme.layout_hero === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDraftTheme((prev) => ({ ...prev, layout_hero: opt.value }))}
                        className={`flex flex-col text-left rounded-lg border p-2.5 transition-all text-xs ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-1 ring-[var(--primary)] shadow-2xs'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between font-semibold text-slate-900">
                          <span className="truncate">{opt.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />}
                        </div>
                        <p className="mt-1 text-[11px] leading-tight text-slate-500 line-clamp-2">
                          {opt.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3. Product Card Slot Variant */}
              <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                    <ListTree className="h-3.5 w-3.5 text-slate-500" />
                    Produktkarten-Stil (Card Slot)
                  </label>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {draftTheme.product_card_variant}
                  </Badge>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {CARD_SLOT_OPTIONS.map((opt) => {
                    const isSelected = draftTheme.product_card_variant === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDraftTheme((prev) => ({ ...prev, product_card_variant: opt.value }))}
                        className={`flex flex-col text-left rounded-lg border p-2.5 transition-all text-xs ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-1 ring-[var(--primary)] shadow-2xs'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between font-semibold text-slate-900">
                          <span className="truncate">{opt.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />}
                        </div>
                        <p className="mt-1 text-[11px] leading-tight text-slate-500 line-clamp-2">
                          {opt.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 4. Filter Layout Slot Variant */}
              <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                    <PanelLeft className="h-3.5 w-3.5 text-slate-500" />
                    Filter- & Navigationslayout (Filter Slot)
                  </label>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {draftTheme.filter_layout}
                  </Badge>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {FILTER_SLOT_OPTIONS.map((opt) => {
                    const isSelected = draftTheme.filter_layout === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setDraftTheme((prev) => ({ ...prev, filter_layout: opt.value }))}
                        className={`flex flex-col text-left rounded-lg border p-2.5 transition-all text-xs ${
                          isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-1 ring-[var(--primary)] shadow-2xs'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between font-semibold text-slate-900">
                          <span className="truncate">{opt.label}</span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />}
                        </div>
                        <p className="mt-1 text-[11px] leading-tight text-slate-500">
                          {opt.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Global Margenaufschlag (pricing_markup_percent) */}
          <Card className="border-[var(--border)] bg-white shadow-xs">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[var(--primary)]" />
                <CardTitle className="text-sm font-bold">Globale Marge & Preisaufschlag</CardTitle>
              </div>
              <CardDescription className="text-xs">
                Globaler prozentualer Aufschlag auf ASL-Großhandels-Einkaufspreise für diesen Mandanten.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-800">
                    Mandanten-Margenaufschlag (%)
                  </label>
                  <span className="text-xs font-mono font-bold text-[var(--primary)]">
                    +{Number(draftTheme.pricingMarkupPercent || 0).toFixed(1)} %
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max="200"
                      value={draftTheme.pricingMarkupPercent}
                      onChange={(e) => setDraftTheme((prev) => ({ ...prev, pricingMarkupPercent: e.target.value }))}
                      className="pr-8 text-xs font-mono"
                      placeholder="15.0"
                    />
                    <span className="pointer-events-none absolute right-3 top-2 text-xs font-semibold text-slate-400">
                      %
                    </span>
                  </div>
                </div>

                {/* Quick Presets */}
                <div className="flex items-center gap-1.5 pt-1">
                  {[0, 10, 15, 20, 25, 30].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setDraftTheme((prev) => ({ ...prev, pricingMarkupPercent: pct }))}
                      className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-all ${
                        Number(draftTheme.pricingMarkupPercent) === pct
                          ? 'bg-[var(--primary)] text-white shadow-2xs'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      +{pct}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Dynamic Live Price Calculation Example */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
                <div className="font-semibold text-slate-800 text-[11px] uppercase tracking-wider">
                  Muster-Kalkulation (B2B Netto)
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-white p-2 border border-slate-200">
                    <span className="block text-[10px] text-slate-500">ASL Basis-EK</span>
                    <span className="font-mono font-bold text-slate-900">200,00 €</span>
                  </div>
                  <div className="rounded-md bg-white p-2 border border-slate-200">
                    <span className="block text-[10px] text-slate-500">Marge</span>
                    <span className="font-mono font-bold text-emerald-600">
                      +{(200 * (parseFloat(draftTheme.pricingMarkupPercent) || 0) / 100).toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                  <div className="rounded-md bg-[var(--primary)]/10 p-2 border border-[var(--primary)]/20">
                    <span className="block text-[10px] text-[var(--primary)] font-semibold">Shop-VK</span>
                    <span className="font-mono font-bold text-[var(--primary)]">
                      {(200 * (1 + (parseFloat(draftTheme.pricingMarkupPercent) || 0) / 100)).toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 leading-tight">
                  Hinweis: Individuelle SKU-Festpreise im Sortiment-Tab überschreiben diesen globalen Standardaufschlag.
                </p>
              </div>
            </CardContent>

            <CardFooter className="flex items-center justify-between border-t border-[var(--border)] bg-slate-50 px-6 py-3">
              <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1.5 text-xs text-slate-600">
                <RotateCcw className="h-3.5 w-3.5" />
                Zurücksetzen
              </Button>

              <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 text-xs">
                {saving ? (
                  <span className="flex items-center gap-1.5">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Speichere...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Save className="h-3.5 w-3.5" />
                    Theme speichern
                  </span>
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Right Column: Live Mockup Preview (7 cols) */}
        <div className="lg:col-span-7">
          <LiveShopMockup
            tenant={currentTenant}
            draftTheme={draftTheme}
          />
        </div>
      </div>
    </div>
  );
}
